/**
 * Verify-triage — the reflection step between "scoped tests fail after a
 * behaviour edit" and "send everything to the fix loop".
 *
 * A user-requested behaviour change can make pinning tests fail BY DESIGN
 * (the test asserts the OLD behaviour the request replaces). Blindly fixing
 * those failures makes the fix loop fight the user's own request (first
 * targeted-edit eval: a wrap-around change broke clamp-pinning movement
 * specs, and the fix iterations started "restoring" the clamping). For each
 * failing test, ONE batched 'analysis' call classifies:
 *
 *   stale-update — the test pins behaviour the request explicitly changed →
 *                  rewrite its assertions to the new behaviour
 *   stale-remove — the request removes the feature the test covers entirely →
 *                  delete that test (the rest of the spec survives)
 *   broken-code  — the edit is wrong/incomplete; the test SHOULD pass →
 *                  goes to the fix loop
 *
 * Accuracy rules are enforced in CODE, not just prompted:
 *   - a stale verdict with no `requestEvidence` (a quoted request phrase that
 *     invalidates the test) is demoted to broken-code;
 *   - failures the model didn't return a verdict for default to broken-code;
 *   - failures in specs that were ALREADY rewritten once this verify pass are
 *     forced to broken-code (the staleness hypothesis was wrong — never
 *     rewrite the same guardrail twice);
 *   - a triage call failure degrades to all-broken-code (yesterday's
 *     behaviour, never silence).
 */
import type { ChangeRequest } from '../edit/types';
import type { FailingTest, LLMConfig, ProjectFile, EventEmitter } from '../../types';
import type { StreamLlmCall } from '../../testing/mockLlm';
import type { ContractGraph } from '../contractGraph';
import { filesExercisedBySpec, isSpecPath } from '../contractGraph';
import { SearchReplaceStrategy } from '../editStrategies/SearchReplaceStrategy';
import { extractTestBodies } from '../fix/contextBudget';
import { callLLMStream, type SchemaSpec } from '../../llm';
import { tolerantJsonParse } from '../../tolerantJson';
import { stripSlash } from '../pathUtils';
import { SETUP_PRECONDITION_RULES } from '../../prompts/planGuidance';

export type TriageClass = 'stale-update' | 'stale-remove' | 'broken-code';

export interface TriageVerdict {
  file: string;
  testName: string;
  verdict: TriageClass;
  /** Verbatim request phrase justifying staleness ('' ⇒ not stale). */
  requestEvidence: string;
  reason: string;
  /** For stale-update: what the rewritten assertions must pin instead. */
  rewriteInstruction?: string;
}

export interface TriageResult {
  /** Failures the fix loop should treat as real code defects. */
  broken: FailingTest[];
  /** specPath → its stale verdicts (updates + removals together). */
  staleBySpec: Map<string, TriageVerdict[]>;
  verdicts: TriageVerdict[];
}

const TRIAGE_SCHEMA: SchemaSpec = {
  name: 'verify_triage',
  schema: {
    type: 'object',
    properties: {
      verdicts: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            file: { type: 'string' },
            testName: { type: 'string' },
            verdict: { type: 'string', enum: ['stale-update', 'stale-remove', 'broken-code'] },
            requestEvidence: { type: 'string' },
            reason: { type: 'string' },
            rewriteInstruction: { type: 'string' },
          },
          required: ['file', 'testName', 'verdict', 'requestEvidence', 'reason', 'rewriteInstruction'],
          additionalProperties: false,
        },
      },
    },
    required: ['verdicts'],
    additionalProperties: false,
  },
};

const SYSTEM = `Triage failing tests after a BEHAVIOUR-CHANGE edit to a game. For each failing
test, pick ONE verdict — it decides whether the pipeline rewrites the test or fixes the code:

- stale-update: the test pins the OLD behaviour the request changed → rewrite its assertions
  (give rewriteInstruction: what the new assertions must check).
- stale-remove: the request deletes the feature entirely → drop the test (rare).
- broken-code: the edit is wrong/incomplete; the test SHOULD still pass → fix the code.

RULES (apply in order):
1. Uncertain → broken-code. Repinning a good test erases a guardrail; a wrong code-fix is recoverable.
2. stale needs PROOF: requestEvidence must quote, verbatim, the request phrase that invalidates
   the test. No quote → broken-code.
3. failureKind "thrown in PRODUCTION code" → broken-code, always. The new code threw; a stale
   test fails an ASSERTION, it never makes production code throw. Ignore any request phrase.
4. failureKind "thrown INSIDE the spec" → judge the body: a test typo / bad API call is a
   defective TEST (don't repin); a symbol the edit removed is broken-code.
5. A test that never reaches the changed behaviour (asserts far from the altered boundary) is
   NOT stale — if it fails, the code is broken.

Output one verdict per failing test, matching file + testName exactly.`;

export async function triageVerifyFailures(args: {
  request: ChangeRequest;
  planSummary: string;
  flaggedSpecPaths: readonly string[];
  failures: readonly FailingTest[];
  workingFiles: readonly ProjectFile[];
  changedFiles: readonly { file: string }[];
  graph: ContractGraph;
  /** Specs already rewritten this verify pass — their failures are forced to broken-code. */
  rewrittenSpecs: ReadonlySet<string>;
  llm: LLMConfig;
  llmCall?: StreamLlmCall;
  emit: EventEmitter;
  nodeId: string;
  signal?: AbortSignal;
}): Promise<TriageResult> {
  const call = args.llmCall ?? callLLMStream;
  const byFile = new Map<string, ProjectFile>(
    args.workingFiles.map((f) => [stripSlash(f.path), f]),
  );
  const changedSet = new Set(args.changedFiles.map((c) => stripSlash(c.file)));

  // A thrown runtime exception is a broken-CODE signal ONLY when it originates
  // in PRODUCTION code (a non-spec game/system file): a behaviour edit makes
  // assertions stale, it does not make production code throw. But a throw can
  // also come from the SPEC itself (a bad repin/typo, or an import the edit
  // removed) — that is NOT auto-broken-code, so we attribute the throw to its
  // ORIGIN frame (errorFrames is top-first, normalized /virtual/path:LINE:COL)
  // and only force the broken-code reading when the origin is non-spec.
  const THROWN_RE =
    /TypeError|ReferenceError|is not a function|is not defined|Cannot read propert|Cannot access|undefined is not|null is not|NaN|Infinity|Maximum call stack|Unexpected (token|identifier)|timed? ?out/i;
  const frameFile = (frame: string) => stripSlash(frame.replace(/:\d+:\d+$/, ''));
  // Origin of a thrown failure = its first project stack frame. We only force
  // the broken-code reading when the throw originates in a NON-spec (production)
  // file: that is unambiguously the new code throwing. A throw whose top frame
  // is the SPEC (a bad repin/typo) or that has no frame info is left to the
  // model — it could be a defective test OR an import the edit removed.
  const productionThrowOriginOf = (f: FailingTest): string | null => {
    if (!THROWN_RE.test(f.error)) return null;
    const originFrame = (f.errorFrames ?? []).map(frameFile).find((p) => p.length > 0);
    return originFrame && !isSpecPath(originFrame) ? originFrame : null;
  };
  const failureBlocks = args.failures.map((f, i) => {
    const specPath = stripSlash(f.file);
    const spec = byFile.get(specPath);
    const body = spec
      ? extractTestBodies(spec.content, [f.testName], { maxLines: 120 })
      : '(spec source unavailable)';
    const exercisesChanged = filesExercisedBySpec(args.graph, specPath).filter((p) =>
      changedSet.has(stripSlash(p)),
    );
    const threw = THROWN_RE.test(f.error);
    const prodOrigin = productionThrowOriginOf(f);
    const originFrame = (f.errorFrames ?? []).map(frameFile).find((p) => p.length > 0);
    const failureKind = !threw
      ? 'assertion mismatch'
      : prodOrigin
        ? `thrown in PRODUCTION code (${prodOrigin}) — broken-code, never stale`
        : originFrame
          ? `thrown INSIDE the spec (${originFrame}) — judge from the body, NOT auto-broken`
          : 'thrown, origin unknown (no stack frame) — judge from the body';
    return [
      `### Failure ${i + 1}`,
      `file: ${specPath}`,
      `testName: ${f.testName}`,
      `failureKind: ${failureKind}`,
      `error: ${f.error.split('\n')[0].slice(0, 300)}`,
      `exercises changed file(s): ${exercisesChanged.join(', ') || '(none via graph)'}`,
      `test body:`,
      body,
    ].join('\n');
  });

  const user = [
    '## THE USER REQUESTED',
    args.request.request,
    '',
    '## WHAT THE EDIT DID',
    args.planSummary || '(no plan summary)',
    `changed files: ${[...changedSet].join(', ')}`,
    args.flaggedSpecPaths.length > 0
      ? `the plan flagged these specs as pinning the old behaviour: ${args.flaggedSpecPaths.join(', ')}`
      : '',
    '',
    '## FAILING TESTS TO CLASSIFY',
    ...failureBlocks,
  ].join('\n');

  let verdicts: TriageVerdict[] = [];
  try {
    const raw = await call({
      system: SYSTEM,
      user,
      ...args.llm,
      purpose: 'analysis',
      schema: TRIAGE_SCHEMA,
      signal: args.signal,
      onChunk: () => {},
    });
    const parsed = tolerantJsonParse(raw).value as { verdicts?: unknown } | null;
    if (parsed && Array.isArray(parsed.verdicts)) {
      verdicts = parsed.verdicts.filter(
        (v: any): v is TriageVerdict =>
          v &&
          typeof v.file === 'string' &&
          typeof v.testName === 'string' &&
          (v.verdict === 'stale-update' || v.verdict === 'stale-remove' || v.verdict === 'broken-code'),
      );
    }
  } catch (err) {
    console.warn(
      '[verify-triage] call failed — treating every failure as broken-code:',
      err instanceof Error ? err.message : String(err),
    );
  }

  const verdictFor = new Map<string, TriageVerdict>();
  for (const v of verdicts) verdictFor.set(`${stripSlash(v.file)}::${v.testName}`, v);

  const broken: FailingTest[] = [];
  const staleBySpec = new Map<string, TriageVerdict[]>();
  const finalVerdicts: TriageVerdict[] = [];
  for (const f of args.failures) {
    const key = `${stripSlash(f.file)}::${f.testName}`;
    let v = verdictFor.get(key);
    const prodThrowOrigin = productionThrowOriginOf(f);
    // Conservative demotions — enforced here, not just prompted:
    if (!v) {
      v = { file: f.file, testName: f.testName, verdict: 'broken-code', requestEvidence: '', reason: 'no triage verdict returned' };
    } else if (v.verdict !== 'broken-code' && prodThrowOrigin) {
      // A throw originating in PRODUCTION code is the new code failing, not a
      // stale assertion — repinning would erase a guardrail that caught a real
      // defect. Force broken-code regardless of the model's verdict. (A throw
      // inside the SPEC is deliberately NOT forced — see productionThrowOriginOf.)
      v = { ...v, verdict: 'broken-code', reason: `${v.reason} [demoted: exception thrown in production code ${prodThrowOrigin} — not a stale test]` };
    } else if (v.verdict !== 'broken-code' && v.requestEvidence.trim() === '') {
      v = { ...v, verdict: 'broken-code', reason: `${v.reason} [demoted: stale verdict lacked request evidence]` };
    } else if (v.verdict !== 'broken-code' && args.rewrittenSpecs.has(stripSlash(f.file))) {
      v = { ...v, verdict: 'broken-code', reason: `${v.reason} [demoted: spec already rewritten once — staleness hypothesis failed]` };
    }
    finalVerdicts.push(v);
    if (v.verdict === 'broken-code') {
      broken.push(f);
    } else {
      const spec = stripSlash(f.file);
      const list = staleBySpec.get(spec) ?? [];
      list.push(v);
      staleBySpec.set(spec, list);
    }
  }

  const counts = { update: 0, remove: 0, broken: broken.length };
  for (const v of finalVerdicts) {
    if (v.verdict === 'stale-update') counts.update += 1;
    if (v.verdict === 'stale-remove') counts.remove += 1;
  }
  await args.emit({
    type: 'node:activity',
    id: args.nodeId,
    message:
      `triage: ${counts.update} stale-update, ${counts.remove} stale-remove, ${counts.broken} broken-code` +
      (counts.update + counts.remove > 0
        ? ` — stale: ${[...staleBySpec.keys()].join(', ')}`
        : ''),
  });

  return { broken, staleBySpec, verdicts: finalVerdicts };
}

const REWRITE_SCHEMA: SchemaSpec = {
  name: 'spec_rewrite',
  schema: {
    type: 'object',
    properties: { newCode: { type: 'string' } },
    required: ['newCode'],
    additionalProperties: false,
  },
};

const REWRITE_SYSTEM = `You rewrite ONE sandbox test spec file after a user-requested behaviour change
made some of its tests stale. You receive the full spec source, the user's request, and per-test
verdicts. Rules:
- For each "stale-update" test: change ONLY the assertion expressions to pin the NEW behaviour per the
  rewriteInstruction. Keep its label meaningful.
- For each "stale-remove" test: delete that test entry entirely.
- MINIMAL EDIT. Do NOT add new variables, capture objects, helper functions, or steps EXCEPT as needed
  to establish a precondition the rewritten assertion now requires (see the setup rules below). Make
  the smallest change that makes the assertion correct. Over-editing introduces bugs that THROW at
  runtime and get the whole rewrite rejected — the most common failure here.
- If the new behaviour makes the exact post-state hard to compute (a value that WRAPS, or is otherwise
  non-deterministic), assert the WEAKER invariant that definitely holds — the value CHANGED, or stays
  within bounds — instead of a precise number you'd have to derive. A passing weaker assertion beats a
  precise one that's wrong.
- If the rewritten assertion's precondition (a specific position, heading, count, or other state) is
  NOT already established by the test's existing steps, you MAY add or adjust that ONE test's \`setup\`
  phase and \`./helpers/\` imports to establish it — that is NOT "touching other tests," it is what the
  rewrite requires. Never fake the precondition by pressing an illegal input (e.g. a 180-degree reversal)
  or by guessing a fixed \`atTime\` and hoping the state lines up; drive to it deterministically per the
  rules below.
${SETUP_PRECONDITION_RULES}
- Touch NOTHING else: every other test, import, and helper stays byte-identical (aside from a NEW
  \`./helpers/\` import you add under the rule above).
- Keep the defineSandboxTest structure valid. Output the COMPLETE new file content.`;

/** Rewrite one stale spec. Returns the new content, or null on failure (the
 *  caller then routes those failures to the fix loop instead). */
export async function rewriteStaleSpec(args: {
  specPath: string;
  specContent: string;
  request: ChangeRequest;
  verdicts: readonly TriageVerdict[];
  /** Set on a retry after a rejected rewrite: the failed attempt and why it
   *  was rejected (load failure, runtime exception, or dropped tests), so the
   *  model fixes the real problem instead of reproducing it. The field name is
   *  historical (`loadError`); the value is any validation error. */
  previousAttempt?: { code: string; loadError: string };
  llm: LLMConfig;
  llmCall?: StreamLlmCall;
  signal?: AbortSignal;
}): Promise<string | null> {
  const call = args.llmCall ?? callLLMStream;
  const user = [
    '## THE USER REQUESTED',
    args.request.request,
    '',
    '## VERDICTS FOR THIS SPEC',
    ...args.verdicts.map(
      (v) =>
        `- [${v.verdict}] ${v.testName}\n  evidence: "${v.requestEvidence}"\n  ${v.rewriteInstruction ? `rewrite to pin: ${v.rewriteInstruction}` : 'remove this test'}`,
    ),
    '',
    ...(args.previousAttempt
      ? [
          '## YOUR PREVIOUS REWRITE WAS REJECTED',
          `error: ${args.previousAttempt.loadError}`,
          'Fix the real problem (load, runtime, or dropped tests) — output the complete corrected file.',
          '',
        ]
      : []),
    `## CURRENT SPEC (${args.specPath})`,
    args.specContent,
  ].join('\n');
  try {
    const raw = await call({
      system: REWRITE_SYSTEM,
      user,
      ...args.llm,
      purpose: 'code',
      schema: REWRITE_SCHEMA,
      signal: args.signal,
      onChunk: () => {},
    });
    const parsed = tolerantJsonParse(raw).value as { newCode?: unknown } | null;
    if (parsed && typeof parsed.newCode === 'string' && parsed.newCode.trim().length > 0) {
      return parsed.newCode;
    }
  } catch (err) {
    console.warn(
      `[verify-triage] spec rewrite failed for ${args.specPath}:`,
      err instanceof Error ? err.message : String(err),
    );
  }
  return null;
}

const SURGICAL_REPIN_SYSTEM = `You repin the STALE ASSERTIONS in ONE sandbox test spec after a
user-requested behaviour change. For each listed stale test, change ONLY its assertion expressions to
match the NEW behaviour per the rewriteInstruction.
- MINIMAL: do not add variables, helpers, or steps; do not touch any other test, import, or label.
- When the new behaviour makes the exact post-state hard to compute (a value that WRAPS or is
  non-deterministic), assert the WEAKER invariant that definitely holds — the value CHANGED, or stays
  within bounds — not a precise number you'd have to derive. A passing weaker assertion beats a
  precise one that's wrong.
- ESCAPE HATCH — you can ONLY edit assertion lines here, never add a \`setup\` phase, a helper import,
  or drive new input. So for EACH listed test first check: do its EXISTING steps already establish the
  precondition the repinned assertion needs (the position/heading/count it asserts about)? If any ONE
  of them does NOT — the precondition is missing and can only be established by adding setup/steps —
  do NOT invent an illegal input (e.g. a 180-degree reversal) or a fixed-atTime guess to fake it.
  Instead return \`{"blocks": []}\` (no edits at all, for the whole call) so the caller falls back to a
  whole-file rewrite, which IS allowed to add \`setup\`. Only repin surgically when EVERY listed test's
  precondition is already reachable from its current steps.
${new SearchReplaceStrategy().buildPromptAddition()}`;

/**
 * Surgical repin: edit ONLY the failing assertion lines via search/replace
 * blocks, instead of regenerating the whole spec. Preserves the spec's
 * structure (helpers, multi-client setup, every other test) byte-for-byte, so
 * it can't introduce the over-engineered capture/restructure bugs that get a
 * whole-file rewrite rejected. Returns the patched content, or null if it
 * can't be produced/applied (caller falls back to the whole-file rewrite).
 * Handles stale-UPDATE only — a stale-REMOVE (delete a whole test) is left to
 * the whole-file path, where deletion is unambiguous.
 */
export async function surgicalRepinSpec(args: {
  specPath: string;
  specContent: string;
  request: ChangeRequest;
  verdicts: readonly TriageVerdict[];
  llm: LLMConfig;
  llmCall?: StreamLlmCall;
  signal?: AbortSignal;
}): Promise<string | null> {
  const updates = args.verdicts.filter((v) => v.verdict === 'stale-update');
  // Removal needs to drop a whole test entry — brittle as search/replace; the
  // whole-file rewrite handles it. Only go surgical for pure assertion updates.
  if (updates.length === 0 || updates.length !== args.verdicts.length) return null;
  const call = args.llmCall ?? callLLMStream;
  const strategy = new SearchReplaceStrategy();
  const user = [
    '## THE USER REQUESTED',
    args.request.request,
    '',
    '## STALE TESTS TO REPIN (change only their assertions)',
    ...updates.map((v) => `- ${v.testName}: ${v.rewriteInstruction || 'pin the new behaviour'}`),
    '',
    `## CURRENT SPEC (${args.specPath})`,
    args.specContent,
  ].join('\n');
  try {
    const raw = await call({
      system: SURGICAL_REPIN_SYSTEM,
      user,
      ...args.llm,
      purpose: 'code',
      schema: strategy.schema,
      signal: args.signal,
      onChunk: () => {},
    });
    const res = strategy.apply(raw, args.specContent, args.specPath);
    if (res.ok && res.newCode.trim().length > 0 && res.newCode !== args.specContent) {
      return res.newCode;
    }
  } catch (err) {
    console.warn(
      `[verify-triage] surgical repin failed for ${args.specPath}:`,
      err instanceof Error ? err.message : String(err),
    );
  }
  return null;
}
